import { SvgIcon } from '@mui/material';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteBinIcon from '@assets/icons/deleteBinIcon.svg?component';
import { CartDrawerDialogs } from '@components/ProductOffers/modals/ShoppingCartDrawer/CartDrawerDialogs';
import { CartDrawerFooter } from '@components/ProductOffers/modals/ShoppingCartDrawer/CartDrawerFooter';
import { CartItemCard } from '@components/ProductOffers/modals/ShoppingCartDrawer/CartItemCard/CartItemCard';
import { BundleCartItemCard } from '@components/ProductOffers/modals/ShoppingCartDrawer/CartItemCard/BundleCartItemCard';
import { EmptyCartState } from '@components/ProductOffers/modals/ShoppingCartDrawer/EmptyCartState';
import { PricingSummary } from '@components/ProductOffers/modals/ShoppingCartDrawer/PricingSummary/PricingSummary';
import { useShoppingCartDrawer } from '@hooks/useShoppingCartDrawer/useShoppingCartDrawer';
import LoadingButton from '@shared/components/buttons/LoadingButton/LoadingButton';
import { SideModalHeader } from '@shared/components/overlays/SideModalHeader/SideModalHeader';

interface ShoppingCartDrawerProps {
  open: boolean;
  onClose: () => void;
  onCheckout: () => void;
  isCheckoutPending?: boolean;
  checkoutError?: boolean;
  onOpenIntakeForm: () => void;
}

export const ShoppingCartDrawer = ({
  open,
  onClose,
  onCheckout,
  isCheckoutPending,
  checkoutError,
  onOpenIntakeForm,
}: ShoppingCartDrawerProps) => {
  const {
    cart,
    isCartLoading,
    isCartMutating,
    equipmentByPlanId,
    plansByCategory,
    sortedItems,
    hasItems,
    monthlyTotal,
    oneTimeTotal,
    handleUpdateChildQuantity,
    handleDeleteItem,
    removeAllDialogOpen,
    setRemoveAllDialogOpen,
    deleteInternetDialogOpen,
    closeDeleteInternetDialog,
    confirmDeleteInternet,
    confirmRemoveAll,
    detailsPlan,
    handleViewDetails,
    closeDetails,
    editPlan,
  } = useShoppingCartDrawer();

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            // Full-page on phones + bigger-mobile; fixed right-side panel only once
            // the viewport is wider than the panel (tablet up), so it never overflows.
            width: { xs: '100%', md: 676 },
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}
      >
        <SideModalHeader title="Your Shopping Cart" onClose={onClose} />

        <Stack sx={{ flex: 1, overflowY: 'auto', padding: 'clamp(20px,5vw,30px)' }} spacing={1.875} useFlexGap>
          {!hasItems ? (
            <EmptyCartState />
          ) : (
            <>
              <Typography variant="heading.subsection">Cart Items</Typography>

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="label.field">All Items ({sortedItems.length})</Typography>
                <LoadingButton
                  variant="cartRemoveAll"
                  startIcon={<SvgIcon component={DeleteBinIcon} color="inherit" />}
                  disabled={isCartMutating}
                  onClick={() => setRemoveAllDialogOpen(true)}
                >
                  Remove All Items
                </LoadingButton>
              </Stack>

              <Stack spacing={2.5} useFlexGap>
                {sortedItems.map(({ item, plan }, i) =>
                  plan?.isBundle ? (
                    <BundleCartItemCard
                      key={item.id ?? i}
                      bundleItem={item}
                      plansByCategory={plansByCategory}
                      equipmentByPlanId={equipmentByPlanId}
                      onDelete={() => handleDeleteItem(item)}
                      onViewMemberDetails={handleViewDetails}
                    />
                  ) : (
                    <CartItemCard
                      key={item.id ?? i}
                      item={item}
                      plan={plan}
                      equipmentList={equipmentByPlanId.get(item.productOffering?.id ?? '') ?? []}
                      onViewDetails={() => handleViewDetails(plan)}
                      onEdit={() => editPlan.startEdit(item)}
                      onDelete={() => handleDeleteItem(item)}
                      onUpdateChildQuantity={handleUpdateChildQuantity}
                    />
                  )
                )}
              </Stack>

              <Divider variant="cartSection" />

              <PricingSummary cart={cart!} plansByCategory={plansByCategory} />
            </>
          )}
        </Stack>

        <CartDrawerFooter
          hasItems={hasItems}
          monthlyTotal={monthlyTotal}
          oneTimeTotal={oneTimeTotal}
          isCartLoading={isCartLoading}
          isCheckoutPending={isCheckoutPending}
          checkoutError={checkoutError}
          onCheckout={onCheckout}
          onOpenIntakeForm={onOpenIntakeForm}
        />
      </Drawer>

      <CartDrawerDialogs
        removeAllDialogOpen={removeAllDialogOpen}
        setRemoveAllDialogOpen={setRemoveAllDialogOpen}
        confirmRemoveAll={confirmRemoveAll}
        deleteInternetDialogOpen={deleteInternetDialogOpen}
        closeDeleteInternetDialog={closeDeleteInternetDialog}
        confirmDeleteInternet={confirmDeleteInternet}
        isCartMutating={isCartMutating}
        detailsPlan={detailsPlan}
        closeDetails={closeDetails}
        editPlan={editPlan}
      />
    </>
  );
};
